import React, { useEffect, useState, useRef } from "react";
import { LOGO_URL, api } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import CategoryCards from "@/components/CategoryCards";
import WaitlistForm from "@/components/WaitlistForm";
import {
  ArrowRight, CheckCircle2, Globe, ShieldCheck, Sparkles,
  Handshake, Truck, Briefcase, ExternalLink,
} from "lucide-react";

function Hero({ onCta }) {
  return (
    <section className="relative overflow-hidden atff-hero-bg text-white">
      <div className="absolute inset-0 atff-grid-mask pointer-events-none" />
      <div className="relative atff-grain" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-14 sm:pb-20">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ATFF" className="h-12 w-12 object-contain rounded-md bg-white p-1" />
            <div className="leading-tight">
              <p className="display font-bold text-[15px]">Africa Trading Free Flow Portal</p>
              <p className="text-[11px] text-orange-300 tracking-wider uppercase">ATFF · A SMCN Initiative</p>
            </div>
          </div>
          <a href="https://www.atfftrade.africa" target="_blank" rel="noopener noreferrer"
             data-testid="hero-website-link"
             className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-orange-200 hover:text-white transition">
            atfftrade.africa <ExternalLink size={12} />
          </a>
        </header>

        <div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] tracking-wider uppercase text-orange-200">
              <Sparkles size={12} /> AfCFTA Digital Trade Forum 2026 · Lagos · 1–2 July 2026
            </div>
            <h1 className="display mt-5 text-[34px] sm:text-5xl lg:text-[58px] font-extrabold leading-[1.05]">
              Join the <span className="text-[#F58220]">ATFF Digital Trade</span> Waitlist
            </h1>
            <p className="mt-5 text-[15px] sm:text-lg text-white/85 max-w-xl leading-relaxed">
              Be part of Africa&apos;s digital trade future. Connect to AfCFTA opportunities, buyers, logistics, finance,
              trade documentation support, Rules of Origin readiness and digital trade partnerships.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                onClick={onCta}
                data-testid="hero-cta-btn"
                className="btn-orange inline-flex items-center gap-2 px-6 sm:px-7 py-3.5 rounded-full font-bold"
              >
                Start Waitlist Registration <ArrowRight size={16} />
              </button>
              <a
                href="#why"
                className="btn-ghost-light inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold"
                data-testid="hero-learn-more"
              >
                Why join? <ArrowRight size={16} />
              </a>
            </div>

            <div className="mt-9 grid grid-cols-3 gap-4 max-w-md">
              {[
                { n: "8", l: "Stakeholder paths" },
                { n: "1", l: "Unified portal" },
                { n: "54", l: "AfCFTA markets" },
              ].map((s, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3.5">
                  <p className="display text-3xl font-extrabold text-[#F58220]">{s.n}</p>
                  <p className="text-[11px] uppercase tracking-wider text-white/70 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-[380px]">
              <div className="absolute -inset-6 rounded-[40px] bg-gradient-to-br from-[#F58220]/30 via-[#2F9E44]/20 to-transparent blur-2xl" />
              <div className="relative rounded-[28px] bg-white p-7 shadow-2xl border border-white/30">
                <img src={LOGO_URL} alt="ATFF Logo" className="w-full h-auto object-contain" />
                <div className="mt-5 grid grid-cols-2 gap-3 text-[#0C1F4A]">
                  <div className="rounded-lg bg-[#FFF1E0] p-3">
                    <Handshake size={18} className="text-[#E96C0F]" />
                    <p className="text-[11px] font-semibold mt-1.5">B2B / B2G Matchmaking</p>
                  </div>
                  <div className="rounded-lg bg-[#E4F6E9] p-3">
                    <Truck size={18} className="text-[#1F7A33]" />
                    <p className="text-[11px] font-semibold mt-1.5">Logistics & RoO</p>
                  </div>
                  <div className="rounded-lg bg-[#E8EEFB] p-3">
                    <Briefcase size={18} className="text-[#0C1F4A]" />
                    <p className="text-[11px] font-semibold mt-1.5">Trade Finance Access</p>
                  </div>
                  <div className="rounded-lg bg-[#FFE0BD] p-3">
                    <Globe size={18} className="text-[#B8540A]" />
                    <p className="text-[11px] font-semibold mt-1.5">Nigeria–EU Corridor</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Intro() {
  return (
    <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <p className="text-[11px] font-bold tracking-widest text-[#E96C0F] uppercase">About ATFF</p>
          <h2 className="display text-3xl sm:text-4xl font-extrabold text-[#0C1F4A] mt-2 leading-tight">
            One digital trade ecosystem for Africa.
          </h2>
        </div>
        <div className="lg:col-span-3 space-y-4 text-[15px] leading-relaxed text-slate-700">
          <p>
            The <strong className="text-[#0C1F4A]">Africa Trading Free Flow Portal (ATFF)</strong> is a Nigeria-led digital trade
            infrastructure platform developed by <strong>Service Management Consultancy Nigeria Ltd (SMCN)</strong> to support
            AfCFTA implementation, SME onboarding, B2B/B2G matchmaking, trade documentation, Rules of Origin readiness,
            logistics coordination, finance access and policy dashboards.
          </p>
          <p>
            ATFF helps SMEs, buyers, investors, logistics providers, finance partners, technology companies, government
            institutions and development partners connect within one digital trade ecosystem.
          </p>
        </div>
      </div>
    </section>
  );
}

function WhyJoin() {
  const items = [
    "Complete full ATFF onboarding",
    "List your products or services",
    "Join B2B / B2G matchmaking opportunities",
    "Connect with buyers, logistics, finance & institutions",
    "Access trade documentation & Rules of Origin support",
    "Participate in post-event onboarding sessions",
    "Explore partnership, investment & technical collaboration",
  ];
  return (
    <section id="why" className="bg-[#FFF8F1] border-y border-[#F2E6D4]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] font-bold tracking-widest text-[#E96C0F] uppercase">Why join</p>
            <h2 className="display text-3xl sm:text-4xl font-extrabold text-[#0C1F4A] mt-2 max-w-2xl leading-tight">
              Why join the ATFF Digital Trade Waitlist?
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[13px] text-slate-600">
            <ShieldCheck size={16} className="text-[#2F9E44]" /> Your data is protected
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
          {items.map((t, i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl bg-white border border-[#F1E4CC] p-4">
              <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#E4F6E9] text-[#1F7A33] shrink-0">
                <CheckCircle2 size={16} />
              </span>
              <p className="text-[14px] text-[#0C1F4A] font-medium leading-snug">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection({ selected, onSelect, scrollRef }) {
  return (
    <section ref={scrollRef} id="categories" className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-bold tracking-widest text-[#E96C0F] uppercase">Step 1</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
        <h2 className="display text-3xl sm:text-4xl font-extrabold text-[#0C1F4A] leading-tight">
          Select your category
        </h2>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Please select the category that best describes you. This will help ATFF understand your interest and direct you
          to the right onboarding or partnership pathway.
        </p>
      </div>
      <div className="mt-8">
        <CategoryCards categories={CATEGORIES} selected={selected} onSelect={onSelect} />
      </div>
    </section>
  );
}

function Confirmation({ submission, onReset }) {
  return (
    <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center fade-up" data-testid="confirmation-section">
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#E4F6E9] text-[#1F7A33] mx-auto">
        <CheckCircle2 size={32} />
      </div>
      <h1 className="display mt-6 text-3xl sm:text-4xl font-extrabold text-[#0C1F4A]">
        Thank You for Joining the ATFF Digital Trade Waitlist
      </h1>
      <p className="mt-4 text-slate-700 leading-relaxed">
        Your information has been received. The ATFF team will review your submission and contact you after the
        AfCFTA Digital Trade Forum 2026 with the appropriate next steps.
      </p>
      <p className="mt-3 text-slate-600 text-sm leading-relaxed">
        Next steps may include full ATFF registration, SME onboarding, partnership discussion, investor meeting,
        technical demo or invitation to a post-event onboarding session.
      </p>
      {submission?.email && (
        <p className="mt-5 text-[13px] text-slate-500">
          A confirmation email with a personalised <strong className="text-[#0C1F4A]">&ldquo;Next Steps&rdquo; PDF</strong> has been sent to <strong className="text-[#0C1F4A]">{submission.email}</strong>.
        </p>
      )}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <a
          href="https://www.atfftrade.africa"
          target="_blank" rel="noopener noreferrer"
          data-testid="visit-homepage-btn"
          className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold"
        >
          Visit ATFF Homepage <ArrowRight size={16} />
        </a>
        <button
          onClick={onReset}
          data-testid="back-to-form-btn"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold border border-slate-300 text-[#0C1F4A] hover:bg-slate-50 transition"
        >
          Submit another response
        </button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0C1F4A] text-white/80 mt-10">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="ATFF" className="h-10 w-10 bg-white rounded p-1" />
            <div>
              <p className="display font-bold text-white text-sm">Africa Trading Free Flow Portal</p>
              <p className="text-[11px] text-orange-300 uppercase tracking-wider">ATFF · SMCN</p>
            </div>
          </div>
          <p className="text-[12px] text-white/60 mt-4 leading-relaxed">
            A Nigeria-led digital trade infrastructure platform supporting AfCFTA implementation across Africa.
          </p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-orange-300">Event</p>
          <p className="text-[13px] mt-2">AfCFTA Digital Trade Forum 2026</p>
          <p className="text-[12px] text-white/60">Lagos · 1–2 July 2026</p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-orange-300">Links</p>
          <ul className="space-y-1 mt-2 text-[13px]">
            <li><a className="hover:text-white" href="https://www.atfftrade.africa" target="_blank" rel="noopener noreferrer">www.atfftrade.africa</a></li>
            <li><a className="hover:text-white" href="/admin/login" data-testid="footer-admin-link">Admin Login</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 text-[12px] text-white/50 flex flex-wrap items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} Service Management Consultancy Nigeria Ltd · All rights reserved.</span>
          <span>ATFF · ADTF 2026 Waitlist</span>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const [selected, setSelected] = useState(null);
  const [submission, setSubmission] = useState(null);
  const categoryRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    // Track page view + QR source
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src") || params.get("source") || "direct";
    api.post("/analytics/view", { source: src, path: "/" }).catch(() => {});
  }, []);

  const scrollToCategories = () => {
    categoryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleSelect = (key) => {
    setSelected(key);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  if (submission) {
    return (
      <>
        <Confirmation submission={submission} onReset={() => { setSubmission(null); setSelected(null); }} />
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Hero onCta={scrollToCategories} />
      <Intro />
      <WhyJoin />
      <CategorySection selected={selected} onSelect={handleSelect} scrollRef={categoryRef} />
      {selected && (
        <section ref={formRef} className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
          <WaitlistForm category={selected} onSuccess={(d) => { setSubmission(d); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        </section>
      )}
      <Footer />
    </div>
  );
}
